import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../services/api/apiService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { POLL_MS, localIsoDate, screenErrorKind } from './screenUtils';

/**
 * Venue screen data for league `leagueId` (id, UUID or short code), from
 * GET /leagues/:id/screen, reloaded every POLL_MS. When this browser has a
 * live socket (signed in), it also joins the live matches' rooms and reloads
 * as soon as a score changes.
 *
 * status: 'loading' | 'ready' | 'private' | 'not_found' | 'error'.
 * A failed reload keeps the last data on screen; `lastUpdated` tells how old it is.
 */
export const useLeagueScreen = (leagueId, { pollMs = POLL_MS } = {}) => {
  const [state, setState] = useState({ status: 'loading', data: null, lastUpdated: null });
  const { connected, joinMatch, leaveMatch, addEventListener } = useWebSocket();
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!leagueId || inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await apiClient.get(`/leagues/${leagueId}/screen`, {
        params: { date: localIsoDate() },
      });
      setState({ status: 'ready', data: res.data?.data || null, lastUpdated: Date.now() });
    } catch (error) {
      const kind = screenErrorKind(error);
      // A network blip keeps the board up; privacy or a deleted league replaces it
      setState((prev) => (prev.data && kind === 'error' ? prev : { status: kind, data: null, lastUpdated: prev.lastUpdated }));
    } finally {
      inFlight.current = false;
    }
  }, [leagueId]);

  useEffect(() => {
    setState({ status: 'loading', data: null, lastUpdated: null });
    load();
    const timer = setInterval(load, pollMs);
    return () => clearInterval(timer);
  }, [load, pollMs]);

  // Live score pushes for the matches in progress (signed-in sockets only)
  const liveIds = (state.data?.live || []).map((m) => m.id).join(',');
  useEffect(() => {
    if (!connected || !liveIds || !joinMatch || !addEventListener) return undefined;
    const ids = liveIds.split(',').map(Number);
    ids.forEach((id) => joinMatch(id));
    let debounce = null;
    const onScore = (payload) => {
      if (!ids.includes(Number(payload?.matchId))) return;
      clearTimeout(debounce);
      debounce = setTimeout(load, 400);
    };
    const offScore = addEventListener('live_score_update', onScore);
    const offEvent = addEventListener('match_event', onScore);
    return () => {
      clearTimeout(debounce);
      if (typeof offScore === 'function') offScore();
      if (typeof offEvent === 'function') offEvent();
      ids.forEach((id) => leaveMatch?.(id));
    };
  }, [connected, liveIds, joinMatch, leaveMatch, addEventListener, load]);

  return { ...state, reload: load };
};

export default useLeagueScreen;
